import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {Box, Button, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import type {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import type {SurveyRedactionMark} from "crqlar-react-common/dist/models/survey/surveyRedaction.model";
import {type SurveyAnswer, SurveyAnswerSentiment} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useMemo} from "react";
import {ElementChip} from "../../common/uiElements/base/ElementChip";
import {NEGATIVE_COLOR, NEUTRAL_COLOR, POSITIVE_COLOR} from "../../insights/kit/insightsColors";
import type {SurveyAnswerContext} from "../shared/surveyAnswer.util";
import {isWithheldSensitiveAnswer, SurveySensitiveAnswerValue} from "../shared/SurveySensitiveAnswerValue";
import SurveyRedactedAnswerValue from "./blackout/SurveyRedactedAnswerValue";
import {
    SURVEY_ANSWER_BLOCK_GAP,
    SurveyAnswerNotAnswered,
    surveyAnswerValueSx,
    SurveyQuestionTitleRow
} from "./surveySubmissionAnswerLayout";

/** The names the AI's theme labels point at, by theme id. */
interface SurveySubmissionThemes {
    names: ReadonlyMap<string, string>;
    loading: boolean;
}

interface SurveySubmissionAnswerListProps {
    questions: SurveyQuestion[];
    /** The copy on screen: the revealed one while sensitive answers are shown. */
    answers: SurveyAnswer[] | undefined;
    /** Always the listed row's. */
    redactionMarks: SurveyRedactionMark[] | undefined;
    ctx: SurveyAnswerContext;
    themes: SurveySubmissionThemes;
}

const SENTIMENT_LABEL_KEYS: Record<SurveyAnswerSentiment, string> = {
    [SurveyAnswerSentiment.POSITIVE]: 'Positive',
    [SurveyAnswerSentiment.NEUTRAL]: 'Neutral',
    [SurveyAnswerSentiment.NEGATIVE]: 'Negative',
};

/** The Sentiment and Themes cards' own colours - one reading of a reply across the whole module. */
const SENTIMENT_COLORS: Record<SurveyAnswerSentiment, string> = {
    [SurveyAnswerSentiment.POSITIVE]: POSITIVE_COLOR,
    [SurveyAnswerSentiment.NEUTRAL]: NEUTRAL_COLOR,
    [SurveyAnswerSentiment.NEGATIVE]: NEGATIVE_COLOR,
};

const THEME_DOT_SIZE = 8;

/*
    The themes the AI heard in a typed answer, beside the answer itself; the answer's overall
    sentiment is deliberately not shown. The row stays out of the blackout editor - that mode is
    about the words - and off a withheld sensitive answer, where a theme chip would leak the
    tone of text the reader is not allowed to see.
*/
const ThemeChipRow = ({answer, themes}: {answer: SurveyAnswer | undefined; themes: SurveySubmissionThemes}) => {
    const {t} = useLocalize();
    const judgements = answer?.aiThemeJudgements ?? [];

    if (!judgements.length) return null;

    return (
        <Stack direction={'row'} flexWrap={'wrap'} alignItems={'center'} gap={0.75} sx={{minWidth: 0}}>
            {/* Held back until the names are in, so a known theme never flashes as its id. */}
            {!themes.loading && judgements.map(judgement => (
                <ElementChip
                    key={`${judgement.themeId}-${judgement.sentiment}`}
                    text={[
                        themes.names.get(judgement.themeId) ?? judgement.themeId,
                        t(SENTIMENT_LABEL_KEYS[judgement.sentiment])
                    ].join(' · ')}
                    startIcon={(
                        <Box
                            sx={{
                                width: THEME_DOT_SIZE,
                                height: THEME_DOT_SIZE,
                                borderRadius: '50%',
                                bgcolor: SENTIMENT_COLORS[judgement.sentiment],
                            }}
                        />
                    )}
                    tooltipContent={t('Survey theme')}
                />
            ))}
        </Stack>
    );
};

/** The note that sensitive answers are withheld, with the button that asks for them. */
export function SurveySubmissionSensitiveBanner({revealed, disabled, onToggle}: {
    revealed: boolean;
    disabled: boolean;
    onToggle: () => void;
}) {
    const {t} = useLocalize();

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            flexWrap={'wrap'}
            gap={1.5}
            sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <LockOutlinedIcon
                sx={{fontSize: 20, color: 'warning.main', flexShrink: 0}}
            />

            <Typography
                variant={'body2'}
                color={'text.secondary'}
                sx={{flex: '1 1 auto', minWidth: 0}}
            >
                {revealed
                    ? t('Sensitive answers are shown.')
                    : t('Sensitive answers are hidden by default.')}
            </Typography>

            <Button
                size={'small'}
                onClick={onToggle}
                disabled={disabled}
                sx={{flexShrink: 0}}
            >
                {revealed ? t('Hide answers') : t('Show answers')}
            </Button>
        </Stack>
    );
}

/**
 * A reply's answers as staff read them: the masked text with its bars, withheld answers locked, and the AI's
 * themes. A Fragment, so the answers sit in the caller's Stack beside its banner.
 */
const SurveySubmissionAnswerList = ({
    questions,
    answers,
    redactionMarks,
    ctx,
    themes
}: SurveySubmissionAnswerListProps) => {
    const {t} = useLocalize();

    const marksByQuestion = useMemo(() => {
        const map = new Map<string, SurveyRedactionMark[]>();

        (redactionMarks ?? []).forEach(mark => {
            if (!mark.questionId) return;
            map.set(mark.questionId, [...(map.get(mark.questionId) ?? []), mark]);
        });

        return map;
    }, [redactionMarks]);

    return (
        <>
            {questions.map(question => {
                const answer = answers?.find(candidate => candidate.questionId === question.id);
                const marks = question.id
                    ? marksByQuestion.get(question.id)
                    : undefined;
                const withheld = isWithheldSensitiveAnswer(answer, question, ctx);

                return (
                    <Stack
                        key={question.id}
                        data-survey-question-id={question.id}
                        tabIndex={-1}
                        gap={SURVEY_ANSWER_BLOCK_GAP}
                        sx={{minWidth: 0}}
                    >
                        <SurveyQuestionTitleRow question={question} ctx={ctx}/>

                        <Typography
                            variant={'body1'}
                            component={'div'}
                            sx={surveyAnswerValueSx}
                        >
                            {withheld ? (
                                <SurveySensitiveAnswerValue
                                    tooltip={t('Hidden sensitive answer. Show answers above to reveal it.')}
                                />
                            ) : (
                                <SurveyRedactedAnswerValue
                                    answer={answer}
                                    question={question}
                                    ctx={ctx}
                                    marks={marks}
                                />
                            )}
                            {!answer && !marks?.length && (
                                <SurveyAnswerNotAnswered label={t('Not answered')}/>
                            )}
                        </Typography>

                        {!withheld && <ThemeChipRow answer={answer} themes={themes}/>}
                    </Stack>
                );
            })}

            {!questions.length && (
                <Typography variant={'body1'} color={'text.secondary'}>
                    {t('This survey has no questions yet.')}
                </Typography>
            )}
        </>
    );
};

export default SurveySubmissionAnswerList;
